import { api, useGetRoutesQuery } from 'app/api';
import { useFieldSlice } from 'app/containers/Field/slice';
import { selectAllRoutesFilters } from 'app/containers/Field/slice/selectors';
import { AllRoutesFiltersType } from 'app/containers/Field/slice/types';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { formatRouteDateFilter } from 'utils/helpers';
import { ALL_ROUTES_POSITION_POLLING_INTERVAL } from '../constants';
import { selectHiddenOnMapGpsVehicleIds } from 'app/containers/Map/slice/selectors';

const defaultDateFilters: AllRoutesFiltersType = {
  startDate: formatRouteDateFilter({
    momentValue: moment(),
    isStartOfDay: true,
  }),
  endDate: formatRouteDateFilter({
    momentValue: moment(),
    isStartOfDay: false,
  }),
};

const useAllRoutesFilters = () => {
  const dispatch = useDispatch();
  const fieldActions = useFieldSlice().actions;

  const envId = useSelector(selectSelectedEnvId) as number;
  const allRoutesFilters = useSelector(selectAllRoutesFilters);

  // final filters
  const apiFinalFilters =
    allRoutesFilters.endDate || allRoutesFilters.startDate
      ? allRoutesFilters
      : defaultDateFilters;

  const setAllRoutesFilters = (newFilters: AllRoutesFiltersType) => {
    dispatch(fieldActions.setAllRoutesFilters(newFilters));
  };

  return { envId, apiFinalFilters, allRoutesFilters, setAllRoutesFilters };
};

export const useGetAllRoutesFromState = () => {
  const hiddenGpsVehicleIds = useSelector(selectHiddenOnMapGpsVehicleIds);

  const { envId, apiFinalFilters } = useAllRoutesFilters();

  const {
    data: routesData,
    isFetching: isRoutesFetching,
  } = api.endpoints.getRoutes.useQueryState({
    envId,
    filters: apiFinalFilters,
    routesInFieldsOnly: false,
  });

  const routes = routesData?.content || [];

  const notHiddenRoutes = routes.filter(
    route => !hiddenGpsVehicleIds.includes(route.gpsVehicle.id),
  );

  return { routes, isRoutesFetching, notHiddenRoutes };
};

export const useLoadAllRoutes = () => {
  const {
    envId,
    setAllRoutesFilters,
    apiFinalFilters,
    allRoutesFilters,
  } = useAllRoutesFilters();

  useGetRoutesQuery(
    {
      envId,
      filters: apiFinalFilters,
      routesInFieldsOnly: false,
    },
    {
      pollingInterval: ALL_ROUTES_POSITION_POLLING_INTERVAL,
    },
  );

  return { allRoutesFilters, setAllRoutesFilters };
};
